// src/components/NearbyHospitals.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadMaps } from "../utils/loadMaps";
import "../styles/NearbyHospitals.css";

export default function NearbyHospitals() {
  const mapElRef = useRef(null);
  const markersRef = useRef([]);
  const placesServiceRef = useRef(null);
  const mapRef = useRef(null);

  const [places, setPlaces] = useState([]);
  const [error, setError] = useState("");
  const [loadingMap, setLoadingMap] = useState(true);

  const navigate = useNavigate();

  // Back handler: mimic browser back, with a safe fallback
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/result");
  };

  // Sort/unique helper
  const normalize = (arr) => {
    const byId = new Map();
    for (const p of arr) {
      if (!p?.place_id) continue;
      if (!byId.has(p.place_id)) byId.set(p.place_id, p);
    }
    return Array.from(byId.values()).sort(
      (a, b) =>
        (b.rating || 0) - (a.rating || 0) ||
        String(a.name).localeCompare(String(b.name))
    );
  };

  const addMarker = (g, position, title) => {
    const m = new g.maps.Marker({ map: mapRef.current, position, title });
    markersRef.current.push(m);
  };

  useEffect(() => {
    let cancelled = false;

    const clearMarkers = () => {
      markersRef.current.forEach((m) => m?.setMap?.(null));
      markersRef.current = [];
    };

    const init = async () => {
      try {
        setLoadingMap(true);
        setError("");

        const g = await loadMaps();
        if (cancelled) return;

        // 1) Get user location
        const coords = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos.coords),
            (err) => reject(err),
            { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 }
          );
        });
        if (cancelled) return;

        const center = { lat: coords.latitude, lng: coords.longitude };

        // 2) Map
        mapRef.current = new g.maps.Map(mapElRef.current, {
          center,
          zoom: 14,
          mapId: process.env.REACT_APP_MAP_ID || undefined,
        });
        addMarker(g, center, "You are here");

        // 3) Places nearby: hospitals
        const svc = (placesServiceRef.current =
          placesServiceRef.current || new g.maps.places.PlacesService(mapRef.current));

        const nearbyReq = {
          location: center,
          radius: 8000,
          type: "hospital",
          // keyword: "clinic urgent care", // optional broadening
        };

        const nearby1 = await new Promise((resolve) => {
          svc.nearbySearch(nearbyReq, (res, status) => {
            if (status === g.maps.places.PlacesServiceStatus.OK || status === "OK") {
              resolve(res || []);
            } else resolve([]);
          });
        });

        const merged = normalize(nearby1);

        clearMarkers();
        const items = merged.map((p) => {
          const loc = p.geometry?.location;
          if (loc) addMarker(g, loc, p.name);
          return {
            placeId: p.place_id,
            name: p.name,
            address: p.formatted_address || p.vicinity || "",
            rating: p.rating || "N/A",
          };
        });

        if (!cancelled) {
          setPlaces(items);
          if (!items.length) setError("No hospitals found nearby.");
        }
      } catch {
        if (!cancelled) {
          setError("Unable to load map or nearby hospitals.");
          setPlaces([]);
        }
      } finally {
        if (!cancelled) setLoadingMap(false);
      }
    };

    init();
    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => m?.setMap?.(null));
      markersRef.current = [];
      placesServiceRef.current = null;
      mapRef.current = null;
    };
  }, []);

  // Text-only guidance points (no images)
  const INSTRUCTIONS = useMemo(
    () => [
      "For life‑threatening emergencies, call your local emergency number immediately.",
      "Go to the ER for severe chest pain, difficulty breathing, stroke symptoms, or major trauma.",
      "Use urgent care for minor fractures, cuts, flu, ear infections, or non‑life‑threatening issues.",
      "Book appointments for routine checkups, chronic condition follow‑ups, or specialist referrals.",
      "Pharmacies can help with prescriptions, refills, and common vaccinations.",
      "Bring ID, insurance card (if available), and a list of current medications/allergies.",
    ],
    []
  );

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="hp-header">
        <div style={{ display: "flex", gap: 10, fontWeight: 700 }}>
          <span>Hospitals</span><span>near</span><span>you</span>
        </div>
        <button className="hp-back-btn" onClick={goBack}>⬅ Back</button>
      </div>

      {/* Map */}
      <div
        ref={mapElRef}
        style={{
          width: "100%",
          height: 450,
          margin: "0 auto 20px auto",
          maxWidth: 1200,
          background: "#0d0d0d",
          borderRadius: 10,
          border: "1px solid #073b4c",
        }}
        aria-busy={loadingMap ? "true" : "false"}
      />

      {/* Below the map */}
      <div className="hp-split">
        {/* Left: list */}
        <div className="hp-panel hp-list">
          <h3>Hospitals near you</h3>
          {loadingMap && <p style={{ opacity: 0.8 }}>Loading map…</p>}
          <ul>
            {places.length ? (
              places.map((h) => (
                <li key={h.placeId}>
                  <div className="name">{h.name}</div>
                  <small className="meta">{h.address}</small>
                  <div className="stars">⭐ {h.rating}</div>
                </li>
              ))
            ) : (
              !loadingMap && <li>{error || "No hospitals found nearby."}</li>
            )}
          </ul>
        </div>

        {/* Right: text guidance (no images) */}
        <div className="hp-panel hp-picks">
          <h3>Common services & quick guidance</h3>
          <ul className="hp-service-list">
            {INSTRUCTIONS.map((t, i) => (
              <li key={i}>✅ {t}</li>
            ))}
          </ul>
          <small style={{ opacity: 0.65, display: "block", marginTop: 10 }}>
            This information is general. In an emergency, call your local emergency number.
          </small>
        </div>
      </div>
    </div>
  );
}